<!-- src/components/admin/AdminVehicleEditor.vue -->
<template>
  <v-card>
    <v-card-title class="primary white--text">
      <v-icon left dark>mdi-car-cog</v-icon>
      {{ isNew ? 'Nouveau véhicule' : 'Modifier le véhicule' }}
      <v-spacer />
      <v-btn icon dark @click="$emit('cancel')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text class="pt-6">
      <v-form ref="form" v-model="valid" lazy-validation>
        <!-- Onglets pour organiser les champs -->
        <v-tabs v-model="activeTab" background-color="transparent" grow>
          <v-tab>
            <v-icon left small>mdi-car</v-icon>
            Véhicule
          </v-tab>
          <v-tab>
            <v-icon left small>mdi-account</v-icon>
            Chauffeur
          </v-tab>
          <v-tab>
            <v-icon left small>mdi-cog</v-icon>
            Paramètres
          </v-tab>
          <v-tab>
            <v-icon left small>mdi-currency-usd</v-icon>
            Limites
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab" class="mt-4">
          <!-- TAB 1: Informations véhicule -->
          <v-tab-item>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.registration_number"
                  label="Immatriculation *"
                  :rules="[rules.required]"
                  outlined
                  dense
                  prepend-inner-icon="mdi-card-text"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="form.vehicle_type"
                  :items="vehicleTypes"
                  label="Type de véhicule"
                  outlined
                  dense
                  clearable
                  prepend-inner-icon="mdi-car-side"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.make"
                  label="Marque *"
                  :rules="[rules.required]"
                  outlined
                  dense
                  prepend-inner-icon="mdi-factory"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.model"
                  label="Modèle"
                  outlined
                  dense
                  prepend-inner-icon="mdi-car-estate"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="form.year"
                  label="Année"
                  type="number"
                  :min="1900"
                  :max="new Date().getFullYear() + 1"
                  outlined
                  dense
                  prepend-inner-icon="mdi-calendar"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="form.color"
                  label="Couleur"
                  outlined
                  dense
                  prepend-inner-icon="mdi-palette"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="form.vin"
                  label="N° Châssis (VIN)"
                  outlined
                  dense
                  prepend-inner-icon="mdi-barcode"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.other_make"
                  label="Autre marque"
                  outlined
                  dense
                  hint="Si la marque n'est pas dans la liste"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.other_model"
                  label="Autre modèle"
                  outlined
                  dense
                  hint="Si le modèle n'est pas dans la liste"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="form.reservoir_capacity"
                  label="Capacité réservoir (L)"
                  type="number"
                  min="0"
                  step="0.01"
                  outlined
                  dense
                  prepend-inner-icon="mdi-gas-station"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.secret_code"
                  label="Code secret"
                  outlined
                  dense
                  prepend-inner-icon="mdi-lock"
                  type="tel"
                  inputmode="numeric"
                  maxlength="4"
                  :rules="[rules.secretCode]"
                  hint="4 chiffres maximum"
                  persistent-hint
                  @input="onSecretCodeInput"
                />
              </v-col>
            </v-row>
          </v-tab-item>

          <!-- TAB 2: Informations chauffeur -->
          <v-tab-item>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.driver_first_name"
                  label="Prénom du chauffeur"
                  outlined
                  dense
                  prepend-inner-icon="mdi-account"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.driver_last_name"
                  label="Nom du chauffeur"
                  outlined
                  dense
                  prepend-inner-icon="mdi-account"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.driver_phone_number"
                  label="Téléphone du chauffeur"
                  outlined
                  dense
                  prepend-inner-icon="mdi-phone"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="form.user"
                  :items="users"
                  item-text="display_name"
                  item-value="id"
                  label="Utilisateur lié"
                  outlined
                  dense
                  clearable
                  prepend-inner-icon="mdi-account-link"
                  :loading="loadingUsers"
                  @focus="loadUsers"
                />
              </v-col>
            </v-row>
          </v-tab-item>

          <!-- TAB 3: Paramètres & Organisation -->
          <v-tab-item>
            <v-row>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="form.company"
                  :items="companies"
                  item-text="name"
                  item-value="pk"
                  label="Société *"
                  :rules="[rules.required]"
                  outlined
                  dense
                  prepend-inner-icon="mdi-domain"
                  :loading="loadingCompanies"
                  @change="onCompanyChange"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="form.department"
                  :items="departments"
                  item-text="name"
                  item-value="id"
                  label="Département"
                  outlined
                  dense
                  clearable
                  prepend-inner-icon="mdi-sitemap"
                  :loading="loadingDepartments"
                  :disabled="!form.company"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="form.product"
                  :items="products"
                  item-text="name"
                  item-value="id"
                  label="Produit par défaut"
                  outlined
                  dense
                  clearable
                  prepend-inner-icon="mdi-fuel"
                  :loading="loadingProducts"
                  :disabled="!form.company"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="form.kmh"
                  label="Kilométrage actuel"
                  type="number"
                  min="0"
                  outlined
                  dense
                  prepend-inner-icon="mdi-speedometer"
                />
              </v-col>
            </v-row>

            <v-divider class="my-4" />

            <v-subheader class="px-0">
              <v-icon left small>mdi-toggle-switch</v-icon>
              Options de contrôle
            </v-subheader>

            <v-row>
              <v-col cols="12" md="6">
                <v-switch
                  v-model="form.is_activated"
                  label="Véhicule activé"
                  color="success"
                  inset
                  hint="Permet l'utilisation du véhicule"
                  persistent-hint
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-switch
                  v-model="form.is_blocked"
                  label="Véhicule bloqué"
                  color="error"
                  inset
                  hint="Bloque temporairement le véhicule"
                  persistent-hint
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-switch
                  v-model="form.is_kmh"
                  label="Kilométrage obligatoire"
                  color="primary"
                  inset
                  hint="Exige la saisie du kilométrage"
                  persistent-hint
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-switch
                  v-model="form.can_take_picture"
                  label="Photo véhicule obligatoire"
                  color="primary"
                  inset
                  hint="Exige une photo du véhicule"
                  persistent-hint
                />
              </v-col>
            </v-row>
          </v-tab-item>

          <!-- TAB 4: Limites financières -->
          <v-tab-item>
            <v-row>
              <v-col cols="12">
                <v-alert type="info" text dense class="mb-4">
                  Définissez les limites de consommation pour ce véhicule.
                  Laissez vide pour aucune limite.
                </v-alert>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="form.monthly_limit"
                  label="Limite mensuelle (XOF)"
                  type="number"
                  min="0"
                  step="100"
                  outlined
                  dense
                  clearable
                  prepend-inner-icon="mdi-cash"
                  hint="Montant maximum par mois"
                  persistent-hint
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="form.current_spending"
                  label="Dépenses actuelles (XOF)"
                  type="number"
                  min="0"
                  step="100"
                  outlined
                  dense
                  prepend-inner-icon="mdi-cash-check"
                  hint="Montant déjà consommé ce mois"
                  persistent-hint
                  :disabled="isNew"
                />
              </v-col>
            </v-row>

            <!-- Barre de progression des dépenses -->
            <v-row v-if="form.monthly_limit && form.monthly_limit > 0">
              <v-col cols="12">
                <div class="d-flex justify-space-between mb-1">
                  <span class="text-caption">Consommation</span>
                  <span class="text-caption font-weight-bold">
                    {{ formatCurrency(form.current_spending || 0) }} / {{ formatCurrency(form.monthly_limit) }}
                  </span>
                </div>
                <v-progress-linear
                  :value="spendingPercentage"
                  :color="spendingColor"
                  height="20"
                  rounded
                >
                  <template v-slot:default>
                    <span class="white--text text-caption font-weight-bold">
                      {{ spendingPercentage.toFixed(0) }}%
                    </span>
                  </template>
                </v-progress-linear>
              </v-col>
            </v-row>

            <v-divider class="my-4" />

            <!-- Bouton reset dépenses -->
            <v-row v-if="!isNew">
              <v-col cols="12">
                <v-btn
                  color="warning"
                  outlined
                  small
                  @click="resetSpending"
                  :loading="resettingSpending"
                >
                  <v-icon left small>mdi-refresh</v-icon>
                  Réinitialiser les dépenses
                </v-btn>
              </v-col>
            </v-row>
          </v-tab-item>
        </v-tabs-items>
      </v-form>
    </v-card-text>

    <v-divider />

    <v-card-actions class="pa-4">
      <v-chip v-if="!isNew" small outlined class="mr-2">
        <v-icon left x-small>mdi-identifier</v-icon>
        {{ value.id }}
      </v-chip>
      <v-chip v-if="value && value.created_at" small outlined>
        <v-icon left x-small>mdi-calendar</v-icon>
        Créé le {{ toDatetime(value.created_at) }}
      </v-chip>
      <v-spacer />
      <v-btn text @click="$emit('cancel')">
        Annuler
      </v-btn>
      <v-btn
        color="primary"
        :loading="saving"
        :disabled="!valid"
        @click="save"
      >
        <v-icon left>mdi-content-save</v-icon>
        Enregistrer
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import AdminServices from "@/services/AdminServices"
import { CompanyService } from "@/services"
import { toDatetime } from "@/utils/datetime"

const VEHICLE_TYPES = [
  'Motorcycle', 'Truck', 'Tricycle', 'Sedan', 'SUV', 'PICK-UP', 'OTHER', 'VL'
]

export default {
  name: "AdminVehicleEditor",
  props: {
    value: { type: Object, default: null }
  },
  data() {
    return {
      activeTab: 0,
      valid: true,
      saving: false,
      resettingSpending: false,
      
      // Listes de référence
      companies: [],
      departments: [],
      products: [],
      users: [],
      loadingCompanies: false,
      loadingDepartments: false,
      loadingProducts: false,
      loadingUsers: false,

      vehicleTypes: VEHICLE_TYPES,

      // Formulaire
      form: {
        registration_number: "",
        vehicle_type: null,
        make: "",
        model: "",
        other_make: "",
        other_model: "",
        year: null,
        color: "",
        vin: "",
        secret_code: "",
        reservoir_capacity: null,
        driver_first_name: "",
        driver_last_name: "",
        driver_phone_number: "",
        company: null,
        department: null,
        product: null,
        user: null,
        kmh: null,
        is_activated: true,
        is_blocked: false,
        is_kmh: false,
        can_take_picture: false,
        monthly_limit: null,
        current_spending: 0,
      },

      rules: {
        required: v => !!v || "Ce champ est requis",
        secretCode: v => {
          if (!v) return true
          if (!/^\d+$/.test(v)) return "Le code secret ne doit contenir que des chiffres"
          if (v.length > 4) return "4 chiffres maximum"
          return true
        },
      },
    }
  },
  computed: {
    isNew() {
      return !this.value || !this.value.id
    },
    spendingPercentage() {
      if (!this.form.monthly_limit || this.form.monthly_limit <= 0) return 0
      return Math.min(100, ((this.form.current_spending || 0) / this.form.monthly_limit) * 100)
    },
    spendingColor() {
      if (this.spendingPercentage >= 100) return 'error'
      if (this.spendingPercentage >= 80) return 'warning'
      return 'success'
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(v) {
        if (v) {
          this.populateForm(v)
        } else {
          this.resetForm()
        }
      }
    }
  },
  mounted() {
    this.loadCompanies()
  },
  methods: {
    toDatetime,
    
    formatCurrency(val) {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XOF',
        minimumFractionDigits: 0,
      }).format(val || 0)
    },

    populateForm(v) {
      this.form = {
        registration_number: v.registration_number || "",
        vehicle_type: v.vehicle_type || null,
        make: v.make || "",
        model: v.model || "",
        other_make: v.other_make || "",
        other_model: v.other_model || "",
        year: v.year || null,
        color: v.color || "",
        vin: v.vin || "",
        secret_code: v.secret_code || "",
        reservoir_capacity: v.reservoir_capacity || null,
        driver_first_name: v.driver_first_name || "",
        driver_last_name: v.driver_last_name || "",
        driver_phone_number: v.driver_phone_number || "",
        company: v.company || v.company_id || null,
        department: v.department || v.department_id || null,
        product: v.product || v.product_id || null,
        user: v.user || v.user_id || null,
        kmh: v.kmh || null,
        is_activated: v.is_activated !== false,
        is_blocked: !!v.is_blocked,
        is_kmh: !!v.is_kmh,
        can_take_picture: !!v.can_take_picture,
        monthly_limit: v.monthly_limit || null,
        current_spending: v.current_spending || 0,
      }
      
      // Charger les dépendances si on a une société
      if (this.form.company) {
        this.loadDepartments(this.form.company)
        this.loadProducts(this.form.company)
      }
    },

    resetForm() {
      this.form = {
        registration_number: "",
        vehicle_type: null,
        make: "",
        model: "",
        other_make: "",
        other_model: "",
        year: null,
        color: "",
        vin: "",
        secret_code: "",
        reservoir_capacity: null,
        driver_first_name: "",
        driver_last_name: "",
        driver_phone_number: "",
        company: null,
        department: null,
        product: null,
        user: null,
        kmh: null,
        is_activated: true,
        is_blocked: false,
        is_kmh: false,
        can_take_picture: false,
        monthly_limit: null,
        current_spending: 0,
      }
      this.departments = []
      this.products = []
    },

    async loadCompanies() {
      this.loadingCompanies = true
      try {
        const data = await CompanyService.getAllCompanies()
        this.companies = data || []
      } catch (e) {
        console.error("Erreur chargement sociétés:", e)
      } finally {
        this.loadingCompanies = false
      }
    },

    async loadDepartments(companyId) {
      if (!companyId) {
        this.departments = []
        return
      }
      this.loadingDepartments = true
      try {
        const data = await AdminServices.getCompanyDepartments(this, companyId)
        this.departments = data?.results || data || []
      } catch (e) {
        console.error("Erreur chargement départements:", e)
        this.departments = []
      } finally {
        this.loadingDepartments = false
      }
    },

    async loadProducts(companyId) {
      if (!companyId) {
        this.products = []
        return
      }
      this.loadingProducts = true
      try {
        const data = await AdminServices.getCompanyProducts(this, companyId)
        this.products = data.results || data || []
      } catch (e) {
        console.error("Erreur chargement produits:", e)
        this.products = []
      } finally {
        this.loadingProducts = false
      }
    },

    async loadUsers() {
      if (this.users.length > 0) return // déjà chargé
      this.loadingUsers = true
      try {
        const data = await AdminServices.getUsers(this, { page_size: 1000 })
        this.users = (data.results || data || []).map(u => ({
          ...u,
          display_name: u.fullname || u.email || u.username
        }))
      } catch (e) {
        console.error("Erreur chargement utilisateurs:", e)
      } finally {
        this.loadingUsers = false
      }
    },

    onSecretCodeInput(v) {
      const digits = (v || "").replace(/\D+/g, "").slice(0, 4)
      if (digits !== v) {
        this.$nextTick(() => { this.form.secret_code = digits })
      } else {
        this.form.secret_code = digits
      }
    },

    onCompanyChange(companyId) {
      // Réinitialiser département et produit
      this.form.department = null
      this.form.product = null
      
      if (companyId) {
        this.loadDepartments(companyId)
        this.loadProducts(companyId)
      } else {
        this.departments = []
        this.products = []
      }
    },

    async save() {
      if (!this.$refs.form.validate()) return
      
      this.saving = true
      try {
        const payload = { ...this.form }
        
        // Nettoyer les valeurs nulles/vides
        Object.keys(payload).forEach(key => {
          if (payload[key] === "") payload[key] = null
        })

        if (this.isNew) {
          await AdminServices.createVehicle(this, payload)
        } else {
          await AdminServices.updateVehicle(this, this.value.id, payload)
        }
        
        this.$emit('saved')
      } catch (e) {
        console.error("Erreur sauvegarde:", e)
        const msg = e?.response?.data?.detail 
          || e?.response?.data?.message 
          || Object.values(e?.response?.data || {}).flat().join(', ')
          || "Erreur lors de la sauvegarde"
        this.$store.commit("app/message", { msg, color: "error" })
      } finally {
        this.saving = false
      }
    },

    async resetSpending() {
      if (!this.value?.id) return
      
      this.resettingSpending = true
      try {
        await AdminServices.updateVehicle(this, this.value.id, { current_spending: 0 })
        this.form.current_spending = 0
        this.$store.commit("app/message", { 
          msg: "Dépenses réinitialisées", 
          color: "success" 
        })
      } catch (e) {
        console.error("Erreur reset:", e)
        this.$store.commit("app/message", { 
          msg: "Erreur lors de la réinitialisation", 
          color: "error" 
        })
      } finally {
        this.resettingSpending = false
      }
    },
  }
}
</script>

<style scoped>
.v-card-title {
  word-break: normal;
}
</style>